import { isAxiosError } from 'axios';
import { apiClient } from '@/shared/api/http-client';
import { createApiPath } from '@/shared/api/endpoints';
import { Pageable, PageResponse } from '@/entities/user/api/user.api';

export type TaxInvoiceSourceType = 'SETTLEMENT' | 'EXCEL' | 'MANUAL' | '';
/** 서버 TaxInvoiceIssueStatus 코드 (발행 대기·완료·실패) */
export type TaxInvoiceIssueStatus = 'READY' | 'COMPLETED' | 'FAILED' | string;

export interface TaxInvoiceSearchRequest {
  yearMonth?: string;
  keyword?: string;
  sourceType?: string;
}

export interface TaxInvoiceItem {
  id: number;
  settlementYearMonth: string;
  writeDate: string;
  email: string;
  businessNumber: string;
  companyName: string;
  ceoName: string;
  taxInvoiceEmail: string;
  itemName: string;
  supplyCost: number;
  taxAmount: number;
  fixedAmount: number;
  sourceType: TaxInvoiceSourceType;
  issueStatus: TaxInvoiceIssueStatus;
  createdAt: string;
  updatedAt: string;
}

export interface TaxInvoiceUpsertRequest {
  settlementYearMonth: string;
  writeDate: string;
  email: string;
  businessNumber?: string;
  companyName?: string;
  ceoName?: string;
  taxInvoiceEmail?: string;
  itemName?: string;
  supplyCost?: number;
  taxAmount?: number;
  fixedAmount?: number;
}

/** GET /admin/v1/tax-invoices/export 응답 */
export interface TaxInvoiceExportResponse {
  fileName: string;
  downloadUrl: string;
  expiresAt: string;
  rowCount: number;
}

export interface TaxInvoiceCancelResponse {
  /** 팝빌에서 삭제하고 발행 대기(READY)로 되돌린 ID */
  canceledIds: number[];
  /** 발행 완료(국세청 전송) 등으로 지울 수 없거나 팝빌 호출이 실패해 상태를 그대로 둔 ID */
  failedIds: number[];
}

export const TAX_INVOICE_EXPORT_EMPTY_CODE = 'TAX_INVOICE_EXPORT_EMPTY';

/** 서버가 스트리밍 엑셀을 만들 때까지 기본 10초보다 길게 잡는다 */
export const TAX_INVOICE_EXPORT_TIMEOUT_MS = 900_000;

/**
 * 작성일을 서버 형식(YYYYMMDD)으로 바꾼다.
 * 발급·리셋 API는 8자리만 받아(@Size·@Pattern) DatePicker 값(yyyy-MM-dd)은 400이 난다.
 */
export const toTaxInvoiceWriteDate = (value: string): string =>
  value.replace(/-/g, '');

/**
 * 목록의 작성일(YYYYMMDD, 엑셀 업로드분은 yyyy-MM-dd도 있음)을 DatePicker 값(yyyy-MM-dd)으로 바꾼다.
 * 8자리 날짜가 아니면 빈 문자열.
 */
export const fromTaxInvoiceWriteDate = (value?: string | null): string => {
  const digits = (value ?? '').replace(/\D/g, '');
  if (digits.length !== 8) return '';
  return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6, 8)}`;
};

export const buildTaxInvoiceSearchParams = (
  searchRequest: TaxInvoiceSearchRequest,
): Record<string, string> => {
  const params: Record<string, string> = {};
  if (searchRequest.yearMonth) params.yearMonth = searchRequest.yearMonth;
  if (searchRequest.keyword) params.keyword = searchRequest.keyword;
  if (searchRequest.sourceType) params.sourceType = searchRequest.sourceType;
  return params;
};

/** 조건에 맞는 역발행이 없으면 404 + TAX_INVOICE_EXPORT_EMPTY */
export const isTaxInvoiceExportEmptyError = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;
  const data = error.response?.data as { code?: unknown } | undefined;
  return data?.code === TAX_INVOICE_EXPORT_EMPTY_CODE;
};

export const taxInvoiceService = {
  getTaxInvoices: async (
    params: TaxInvoiceSearchRequest,
    pageable: Pageable,
  ): Promise<PageResponse<TaxInvoiceItem>> => {
    const response = await apiClient.get(createApiPath.taxInvoices.list(), {
      params: {
        ...buildTaxInvoiceSearchParams(params),
        page: pageable.page,
        size: pageable.size,
      },
    });
    return response.data;
  },

  getTaxInvoiceDetail: async (id: number): Promise<TaxInvoiceItem> => {
    const response = await apiClient.get(createApiPath.taxInvoices.detail(id));
    return response.data;
  },

  createTaxInvoice: async (data: TaxInvoiceUpsertRequest) => {
    const response = await apiClient.post(createApiPath.taxInvoices.list(), data);
    return response.data;
  },

  updateTaxInvoice: async (id: number, data: TaxInvoiceUpsertRequest) => {
    const response = await apiClient.put(createApiPath.taxInvoices.detail(id), data);
    return response.data;
  },

  deleteTaxInvoice: async (id: number) => {
    await apiClient.delete(createApiPath.taxInvoices.detail(id));
  },

  bulkDeleteTaxInvoices: async (ids: number[]) => {
    await apiClient.delete(createApiPath.taxInvoices.list(), { data: ids });
  },

  // 작성일자는 서버가 정하므로 발행 요청에는 ids만 보낸다
  issueTaxInvoices: async (ids: number[]) => {
    const response = await apiClient.post(createApiPath.taxInvoices.issue(), {
      ids,
    });
    return response.data;
  },

  /**
   * 팝빌에 올린 역발행 문서를 취소·삭제하고 발행 대기(READY)로 되돌린다.
   * 기존 DELETE 는 우리 DB 행만 지우므로, 팝빌 문서를 지우려면 이 API 를 쓴다.
   * 건별 실패가 있어도 200이며, 발행 완료가 아니거나 팝빌 문서번호가 없는 ID 는 두 목록 어디에도 없다.
   */
  cancelTaxInvoices: async (
    ids: number[],
  ): Promise<TaxInvoiceCancelResponse> => {
    const response = await apiClient.post(createApiPath.taxInvoices.cancel(), {
      ids,
    });
    return response.data;
  },

  uploadTaxInvoices: async (items: TaxInvoiceUpsertRequest[]) => {
    const response = await apiClient.post(createApiPath.taxInvoices.upload(), items);
    return response.data;
  },

  resetPublishedSettlement: async (
    settlementYearMonth: string,
    writeDate: string,
  ) => {
    const response = await apiClient.post(
      createApiPath.taxInvoices.resetPublished(),
      { settlementYearMonth, writeDate: toTaxInvoiceWriteDate(writeDate) },
    );
    return response.data;
  },

  /**
   * 역발행 목록 엑셀.
   * 목록과 같은 필터로 서버가 파일을 만든 뒤 서명 URL만 돌려준다.
   */
  exportTaxInvoices: async (
    searchRequest: TaxInvoiceSearchRequest,
  ): Promise<TaxInvoiceExportResponse> => {
    const response = await apiClient.get<TaxInvoiceExportResponse>(
      createApiPath.taxInvoices.export(),
      {
        params: buildTaxInvoiceSearchParams(searchRequest),
        timeout: TAX_INVOICE_EXPORT_TIMEOUT_MS,
      },
    );
    return response.data;
  },

  getBulkTransferRows: async (yearMonth: string) => {
    const response = await apiClient.get(createApiPath.taxInvoices.bulkTransfer(), {
      params: { yearMonth },
    });
    return response.data as Array<{
      bankName?: string;
      bankCode?: string;
      depositAccountNumber?: string;
      transferAmount?: number;
      depositorName?: string;
      memo?: string;
    }>;
  },
};
